import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styles from "../../styles/crearEmpresaPopup.module.css";
import equipoStyles from "../../styles/equipo.module.css";
import { useTranslation } from "react-i18next";
import useFetch from "../../hooks/useFetch";

const ReactivarEncuestaPopup = ({ onClose, encuesta, onConfirm, idEquipo, idEmpresa, modo = 'reactivar' }) => {
  const { t } = useTranslation();
  const [nuevaFechaFin, setNuevaFechaFin] = useState("");
  const [enviarCorreo, setEnviarCorreo] = useState(false);
  const [empleadosSinCompletar, setEmpleadosSinCompletar] = useState([]);
  const { data: dataEmpleados, execute: executeEmpleados, loading: loadingEmpleados } = useFetch();
  const executeEmpleadosRef = useRef(executeEmpleados);

  const esAmpliar = modo === 'ampliar';

  useEffect(() => {
    executeEmpleadosRef.current = executeEmpleados;
  }, [executeEmpleados]);

  useEffect(() => {
    const fetchEmpleados = async () => {
      if (encuesta && (encuesta.id_encuesta || encuesta.id) && idEquipo && idEmpresa) {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
          const idEncuesta = encuesta.id_encuesta || encuesta.id;
          await executeEmpleadosRef.current(`${apiUrl}/gestionEncuestas/getEmpleadosSinCompletar`, "POST", {
            id_encuesta: idEncuesta,
            id_equipo: idEquipo,
            id_empresa: idEmpresa,
          });
        } catch (error) {
          console.error("Error al obtener empleados sin completar:", error);
        }
      }
    };

    fetchEmpleados();
  }, [encuesta, idEquipo, idEmpresa]);

  useEffect(() => {
    if (dataEmpleados && dataEmpleados.empleados) {
      setEmpleadosSinCompletar(dataEmpleados.empleados || []);
    }
  }, [dataEmpleados]);

  const handleConfirm = () => {
    if (!nuevaFechaFin) {
      alert(t("fechaFinRequerida"));
      return;
    }

    const fechaFinDate = new Date(nuevaFechaFin);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    if (fechaFinDate <= hoy) {
      alert(t("fechaFinDebeSerFutura"));
      return;
    }

    if (esAmpliar && encuesta.fechas) {
      const fechaFinActual = encuesta.fechas.split(' - ')[1];
      if (fechaFinActual) {
        const fechaActualDate = new Date(fechaFinActual + "T00:00:00");
        if (fechaFinDate <= fechaActualDate) {
          alert(t("fechaFinDebeSerPosterior"));
          return;
        }
      }
    }

    const idEncuesta = encuesta.id_encuesta || encuesta.id;
    onConfirm({
      id_encuesta: idEncuesta,
      nueva_fecha_fin: nuevaFechaFin,
      enviar_correo: enviarCorreo,
      id_equipo: idEquipo,
    });
  };

  const handleClickOutside = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Obtener la fecha mínima (mañana o día después de la fecha fin actual si es ampliar)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    if (esAmpliar && encuesta?.fechas) {
      const fechaFinActual = encuesta.fechas.split(' - ')[1];
      if (fechaFinActual) {
        const fechaActual = new Date(fechaFinActual.trim() + "T00:00:00");
        const diaSiguiente = new Date(fechaActual);
        diaSiguiente.setDate(diaSiguiente.getDate() + 1);
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const fechaMin = diaSiguiente > hoy ? diaSiguiente : tomorrow;
        return fechaMin.toISOString().split("T")[0];
      }
    }
    return tomorrow.toISOString().split("T")[0];
  };

  const getPorcentajeColor = (porcentaje) => {
    if (porcentaje === 100) return equipoStyles.greenCircle;
    if (porcentaje > 0) return equipoStyles.yellowCircle;
    return equipoStyles.redCircle;
  };

  return ReactDOM.createPortal(
    <div className={styles.popupOverlay} onClick={handleClickOutside} style={{ zIndex: 1009889891 }}>
      <div className={styles.popupContent} style={{ maxWidth: "800px", maxHeight: "90vh", overflowY: "auto" }}>
        <h2>{esAmpliar ? t("ampliarEncuesta") : t("reactivarEncuesta")}</h2>
        <p style={{ color: "#666", marginBottom: "1.5rem" }}>
          {esAmpliar ? t("ampliarEncuestaDescripcion") : t("reactivarEncuestaDescripcion")}
        </p>

        <div className={styles.label}>
          <label>{t("nuevaFechaFin")}</label>
          <input
            type="date"
            className={styles.input}
            value={nuevaFechaFin}
            onChange={(e) => setNuevaFechaFin(e.target.value)}
            min={getMinDate()}
            required
          />
        </div>

        <div style={{ marginTop: "1.5rem" }}>
          <h3 style={{ fontSize: "1.1rem", color: "#333", marginBottom: "1rem" }}>
            {t("empleadosSinCompletar")}
          </h3>
          {loadingEmpleados ? (
            <p style={{ color: "#666", textAlign: "center", padding: "1rem" }}>
              {t("cargando")}...
            </p>
          ) : empleadosSinCompletar.length === 0 ? (
            <p style={{ color: "#666", textAlign: "center", padding: "1rem" }}>
              {t("todosEmpleadosCompletaron")}
            </p>
          ) : (
            <div 
              style={{ 
                maxHeight: "250px", 
                overflowY: "auto", 
                overflowX: "hidden",
                border: "1px solid #dee2e6", 
                borderRadius: "8px",
                backgroundColor: "#f8f9fa"
              }}
              className="custom-scrollbar"
            >
              <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                  width: 8px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: #f1f1f1;
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background: #888;
                  border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: #555;
                }
              `}</style>
              <table className={styles.equiposTable} style={{ width: "100%", margin: 0, backgroundColor: "#fff" }}>
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                  <tr>
                    <th style={{ textAlign: "left", padding: "12px", borderBottom: "2px solid #dee2e6" }}>{t("mail")}</th>
                    <th style={{ textAlign: "left", padding: "12px", borderBottom: "2px solid #dee2e6" }}>{t("CompletionPercentage")}</th>
                  </tr>
                </thead>
                <tbody>
                  {empleadosSinCompletar.map((empleado) => (
                    <tr key={empleado.id_empleado} style={{ borderBottom: "1px solid #f1f3f5" }}>
                      <td style={{ padding: "12px" }}>{empleado.email}</td>
                      <td style={{ padding: "12px" }}>
                        <div className={equipoStyles.porcentajeCompletadoContainer}>
                          <span className={getPorcentajeColor(empleado.porcentajeCompletado)}></span>
                          <span>{empleado.porcentajeCompletado || 0}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ marginTop: "1.5rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={enviarCorreo}
              onChange={(e) => setEnviarCorreo(e.target.checked)}
              style={{ width: "20px", height: "20px", cursor: "pointer" }}
            />
            <span style={{ fontSize: "1rem", color: "#333" }}>
              {t("enviarCorreoRecordatorio")}
            </span>
          </label>
          <p style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.5rem", marginLeft: "1.75rem" }}>
            {t("enviarCorreoRecordatorioDescripcion")}
          </p>
        </div>

        <div className={styles.popupActions}>
          <button onClick={onClose} className={styles.buttonCancel}>
            {t("cancel")}
          </button>
          <button onClick={handleConfirm} className={styles.buttonCreate}>
            {esAmpliar ? t("ampliar") : t("reactivar")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReactivarEncuestaPopup;

